import { useState, useRef, useEffect } from "react";
import { ThumbsUp, MessageCircle, ExternalLink, MoreVertical, Flag, Trash2, BadgeCheck, Dot } from "lucide-react";
import { Avatar, CategoryBadge, timeAgo, isLikedByUser, isPostOwnedByCurrentUser, PostMediaGrid } from "./CommunityShared";
import { openInExternalBrowser } from "../../Utils/Utilities";
import { SHOW_VIEW_COUNTS_IN_FEED } from "../../Utils/FeatureFlags";
import useHaptics from "../../Hooks/useHaptics";

// ─── Post Card Component ─────────────────────────────────────────────────────
export default function PostCard({ post, currentUser, onOpen, onLikeToggle, onDeletePost, onReportPost, onAgencyClick }) {
  const { vibrate } = useHaptics();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  const [isAnimating, setIsAnimating] = useState(false);

  // Like state lives on `post` itself (Community.jsx owns the optimistic toggle on its posts
  // array) - reading it from props here instead of local state keeps both in sync.
  const liked = isLikedByUser(post, currentUser);
  const likeCount = Number(post?.CntLikes) || 0;
  const isOwnPost = isPostOwnedByCurrentUser(post, currentUser);
  const canDelete = Boolean(isOwnPost || currentUser?.UserID === 1);

  const logoSrc = post?.CreatedAgencyLogo;
  const rawPhotos = post?.Photos?.length ? post.Photos : (post?.Photos ? [post.Photos] : []);
  const photos = rawPhotos.filter((p) => {
    if (!p || typeof p !== "string") return false;
    const s = p.trim();
    if (!s || s === "[]" || s === "{}" || s === "null" || s === "undefined") return false;
    if (s.endsWith("/Uploads/") || s.endsWith("/Uploads") || s === "/Uploads/" || s === "/Uploads") return false;
    return true;
  });
  const hasPhotos = photos.length > 0;
  const detail = post?.Detail || "";
  const postType = post?.Type || post?.PostTypeID || post?.TypeID;
  const city = (post?.City || post?.AgencyCity || post?.CreatedAgencyCity || post?.CreatedCity || "").trim();
  const state = (post?.State || post?.AgencyState || post?.CreatedAgencyState || post?.CreatedState || "").trim();
  const location = [city, state].filter(Boolean).join(", ");

  useEffect(() => {
    const handleClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target))
        setMenuOpen(false);
    };
    if (menuOpen) document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [menuOpen]);

  const handleLike = (e) => {
    e.stopPropagation();
    vibrate("success");
    setIsAnimating(true);
    setTimeout(() => setIsAnimating(false), 400);
    onLikeToggle?.(post);
  };

  const handleVisitStore = (e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    const agencyID = post?.CreatedAgencyID;
    if (agencyID) {
      if (onAgencyClick) {
        onAgencyClick(agencyID, post);
      } else {
        const webstoreUrl = `${window.location.origin}/web/store?m=${agencyID}`;
        openInExternalBrowser(webstoreUrl);
      }
    }
  };

  return (
    <article
      id={post?.PostID ? `community-post-${post.PostID}` : undefined}
      className="bg-sahi-white border border-sahi-border-color rounded-[7px] shadow-xs overflow-hidden transition-all mb-3 flex flex-col"
    >
      {/* Top: Header, Title, Detail */}
      <div>
        {/* User Header */}
        <div className="flex items-center gap-3 p-4">
          <button
            onClick={handleVisitStore}
            className="shrink-0 hover:opacity-80 transition-opacity outline-none bg-transparent border-0 p-0 cursor-pointer"
          >
            <Avatar src={logoSrc} name={post?.CreatedAgencyName} size={42} />
          </button>
          <div
            className="flex-1 min-w-0 cursor-pointer"
            onClick={() => onOpen(post, false)}
          >
            <div className="flex items-center gap-1.5 min-w-0 text-sm">
              <button
                onClick={handleVisitStore}
                className="font-bold text-text-color hover:underline cursor-pointer outline-none bg-transparent border-0 p-0 text-left truncate"
              >
                {post?.CreatedAgencyName}
              </button>
              {post?.IsVerified && (
                <BadgeCheck
                  size={14}
                  className="text-sahi-blue fill-sahi-blue-light shrink-0"
                />
              )}
            </div>
            <div className="flex items-center gap-1.5 mt-0.5 min-w-0 text-xs text-sub-text-color font-medium">
              {post?.CreatedUserName && (
                <span className="truncate max-w-[130px] sm:max-w-[180px]">
                  {post.CreatedUserName}
                </span>
              )}
              {post?.CreatedUserName && location && (
                <Dot size={10} className="text-zinc-400 shrink-0 -mx-1" strokeWidth={5} />
              )}
              {location && (
                <span className="truncate max-w-[130px] sm:max-w-[180px]">
                  {location}
                </span>
              )}
              {(post?.CreatedUserName || location) && post?.CreatedDT && (
                <Dot size={10} className="text-zinc-400 shrink-0 -mx-1" strokeWidth={5} />
              )}
              {post?.CreatedDT && (
                <span className="whitespace-nowrap">
                  {timeAgo(post.CreatedDT)}
                </span>
              )}
              {SHOW_VIEW_COUNTS_IN_FEED && post?.CntViews !== undefined && (
                <>
                  <Dot size={10} className="text-zinc-400 shrink-0 -mx-1" strokeWidth={5} />
                  <span className="whitespace-nowrap">
                    {post?.CntViews || 0} {Number(post?.CntViews) === 1 ? "View" : "Views"}
                  </span>
                </>
              )}
            </div>
          </div>
          <div className="relative shrink-0" ref={menuRef}>
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              className="p-1.5 text-sub-text-color hover:bg-sahi-disable sahi-radius cursor-pointer outline-none bg-transparent border-0 transition-colors"
            >
              <MoreVertical size={18} />
            </button>
            {menuOpen && (
              <div className="absolute right-0 top-full mt-1 w-44 bg-sahi-white border border-sahi-border-color sahi-radius shadow-xl z-10 py-1">
                {canDelete ? (
                  <button
                    className="w-full text-left px-3 py-2 text-xs font-semibold text-sahi-red hover:bg-sahi-red/10 flex items-center gap-2 cursor-pointer outline-none border-0 bg-transparent"
                    onClick={() => {
                      onDeletePost?.(post);
                      setMenuOpen(false);
                    }}
                  >
                    <Trash2 size={14} /> Delete
                  </button>
                ) : (
                  <>
                    <button
                      className="w-full text-left px-3 py-2 text-xs font-semibold text-text-color hover:bg-sahi-disable flex items-center gap-2 cursor-pointer outline-none border-0 bg-transparent"
                      onClick={() => {
                        handleVisitStore();
                        setMenuOpen(false);
                      }}
                    >
                      <ExternalLink size={14} className="text-sub-text-color" /> Visit Profile
                    </button>
                    <button
                      className="w-full text-left px-3 py-2 text-xs font-semibold text-text-color hover:bg-sahi-disable flex items-center gap-2 cursor-pointer outline-none border-0 bg-transparent"
                      onClick={() => {
                        setMenuOpen(false);
                        onReportPost?.(post);
                      }}
                    >
                      <Flag size={14} className="text-sub-text-color" /> Report Misuse
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Title & Body */}
        {(post?.Title || detail || postType) && (
          <div className="px-4 pb-3 cursor-pointer" onClick={() => onOpen(post, false)}>
            {/* Category Badge before Title */}
            {postType && (
              <div className="mb-1.5 sm:mb-2">
                <CategoryBadge type={postType} />
              </div>
            )}
            {post?.Title && (
              <h3 className="text-base font-bold text-text-color leading-snug mb-1.5 line-clamp-2">
                {post.Title}
              </h3>
            )}
            {detail && (
              <div className="relative">
                <p className="text-[13px] sm:text-sm text-sub-text-color leading-normal line-clamp-3">
                  {detail}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Media Images */}
        {hasPhotos && (
          <div
            className="w-full cursor-pointer overflow-hidden"
            onClick={() => onOpen(post, false, 0)}
          >
            <PostMediaGrid
              photos={photos}
              title={post?.Title}
              className="border-t border-sahi-border-color/60"
              onImageClick={(idx, e) => {
                if (e && e.stopPropagation) e.stopPropagation();
                onOpen(post, false, idx);
              }}
            />
          </div>
        )}
      </div>

      {/* Bottom: Actions */}
      <div className="px-2 sm:px-3 py-2.5 border-t border-sahi-border-color/60">
        <style>{`
          @keyframes postLikeNudge {
            0% {
              transform: scale(1);
            }
            40% {
              transform: scale(1.45);
            }
            100% {
              transform: scale(1);
            }
          }
          .animate-post-like-nudge {
            animation: postLikeNudge 400ms cubic-bezier(0.175, 0.885, 0.32, 1.275) both;
            transform-origin: center center;
            will-change: transform;
          }
        `}</style>
        <div className="flex items-center justify-center gap-1.5 sm:gap-2 w-full">
          <button
            onClick={handleLike}
            className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 px-2 sahi-radius text-[13px] font-semibold transition-all duration-300 active:scale-95 cursor-pointer border-0 bg-transparent ${liked ? "text-sahi-green bg-[#0f925c15]" : "text-sub-text-color hover:bg-sahi-disable"}`}
          >
            <ThumbsUp
              size={15}
              strokeWidth={liked ? 2.5 : 2}
              className={`${liked ? "fill-[#0f925c22] text-sahi-green" : "text-sub-text-color"} ${isAnimating ? "animate-post-like-nudge" : "transition-colors duration-300"}`}
            />
            <span className="leading-none">
              {likeCount > 0 ? `${likeCount} ` : ""}
              {likeCount > 1 ? "Likes" : "Like"}
            </span>
          </button>

          <button
            onClick={() => onOpen(post, true)}
            className="flex-1 flex items-center justify-center gap-1.5 py-1.5 px-2 sahi-radius text-[13px] font-semibold text-sub-text-color hover:bg-sahi-disable transition-colors cursor-pointer border-0 bg-transparent"
          >
            <MessageCircle size={15} />
            <span className="leading-none">Reply</span>
          </button>

          <button
            onClick={handleVisitStore}
            className="flex-1 flex items-center justify-center gap-1.5 py-1.5 px-2 sahi-radius text-[13px] font-semibold text-sub-text-color hover:bg-sahi-disable cursor-pointer border-0 bg-transparent transition-colors"
          >
            <ExternalLink size={15} />
            <span className="leading-none">Visit</span>
          </button>
        </div>
      </div>
    </article>
  );
}
